// TODO: Rework the functionality once React Routes is available for all tabs

import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@bringg/react-components';

const isTabLink = (target: Element): HTMLElement | null => {
	const link = target.closest('.nav-tabs li a');
	return link as HTMLElement | null;
};

const isActiveTabLink = (link: HTMLElement): boolean => {
	return link.parentElement?.classList.contains('active') ?? false;
};

const isReserveCapacityTabActive = (): boolean => {
	const page = document.getElementById('reserve-capacity-page');
	return !!page?.closest('.tab-pane.active');
};

const extractAngularUrl = (absUrl: string): string => {
	const hashIdx = absUrl.indexOf('#');
	if (hashIdx >= 0) {
		const fragment = absUrl.slice(hashIdx + 1);
		return fragment.startsWith('!') ? fragment.slice(1) : fragment;
	}
	const parsed = new URL(absUrl);
	return parsed.pathname + parsed.search + parsed.hash;
};

export const useUnsavedChangesGuard = (isDirty: boolean): void => {
	const { t } = useTranslation();
	const skipNextClickRef = useRef(false);
	const allowNextNavigationRef = useRef(false);

	useEffect(() => {
		if (!isDirty) return;

		const showModal = (onOk: () => void) => {
			ConfirmModal({
				title: t('RESERVE_CAPACITY.UNSAVED_CHANGES_TITLE'),
				content: t('RESERVE_CAPACITY.UNSAVED_CHANGES_CONTENT'),
				okText: t('RESERVE_CAPACITY.LEAVE_ANYWAY'),
				cancelText: t('GLOBAL.CANCEL'),
				onOk
			});
		};

		const handleClick = (e: MouseEvent) => {
			if (skipNextClickRef.current) {
				skipNextClickRef.current = false;
				return;
			}

			const link = isTabLink(e.target as Element);
			if (!link || isActiveTabLink(link) || !isReserveCapacityTabActive()) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			showModal(() => {
				skipNextClickRef.current = true;
				allowNextNavigationRef.current = true;
				link.click();
			});
		};

		document.addEventListener('click', handleClick, true);

		const $injector = (window as any).angular?.element(document.body).injector?.();

		if (!$injector) {
			return () => {
				document.removeEventListener('click', handleClick, true);
			};
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const $rootScope = $injector.get('$rootScope') as any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const $location = $injector.get('$location') as any;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const deregister = $rootScope.$on('$locationChangeStart', (event: any, nextUrl: string) => {
			if (allowNextNavigationRef.current) {
				allowNextNavigationRef.current = false;
				return;
			}

			event.preventDefault();

			showModal(() => {
				allowNextNavigationRef.current = true;
				$rootScope.$apply(() => {
					$location.url(extractAngularUrl(nextUrl));
				});
			});
		});

		return () => {
			skipNextClickRef.current = false;
			allowNextNavigationRef.current = false;
			document.removeEventListener('click', handleClick, true);
			deregister();
		};
	}, [isDirty, t]);
};
